import React from 'react'
import { Link } from 'react-router-dom'
import InvoiceGenerator from '../components/InvoiceGenerator'

const CanvaAlternativePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur">
              Better Than Canva for Invoices
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              The Professional Alternative to the Canva Invoice Maker
            </h1>
            <p className="text-2xl text-blue-100 mb-6">
              Stop messing with design layers. Create a compliant, professional invoice in seconds, not hours.
            </p>
            <p className="text-xl text-blue-200">
              <strong>Better than a graphic design tool.</strong> Purpose-built for invoicing.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Comparison Banner */}
      <div className="bg-yellow-50 border-y border-yellow-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <span className="text-2xl">⚠️</span>
            <p className="text-lg text-gray-900">
              <strong>Using Canva for invoices?</strong> You're using a hammer to screw in a bolt. Here's why a dedicated invoice generator is better.
            </p>
          </div>
        </div>
      </div>

      {/* Invoice Generator Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Try the Professional Way</h2>
            <p className="text-lg text-gray-600">Create a compliant invoice in under 60 seconds. No design skills needed.</p>
          </div>
          <InvoiceGenerator />
        </div>

        {/* Head-to-Head Comparison */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Canva Invoice Maker vs. InvoiceMeNow
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Canva Problems */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🎨</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Canva Invoice Maker</h3>
              </div>
              
              <ul className="space-y-4">
                {[
                  { icon: '❌', text: 'Requires Canva account signup', emphasis: 'Mandatory login' },
                  { icon: '❌', text: 'Invoice templates buried in 100,000+ designs', emphasis: 'Hard to find' },
                  { icon: '❌', text: 'Manual text editing in design layers', emphasis: '10-15 minutes per invoice' },
                  { icon: '❌', text: 'No automatic calculation of totals', emphasis: 'You do the math' },
                  { icon: '❌', text: 'Not optimized for tax compliance', emphasis: 'Legal risks' },
                  { icon: '❌', text: 'Can\'t email invoices directly', emphasis: 'Download → email separately' },
                  { icon: '❌', text: 'No payment integration', emphasis: 'Can\'t collect money' },
                  { icon: '❌', text: 'Design-first, not invoice-first', emphasis: 'Wrong tool for the job' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <span className="text-gray-700">{item.text}</span>
                      <span className="block text-sm text-red-600 font-semibold mt-1">→ {item.emphasis}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* InvoiceMeNow Advantages */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl shadow-lg p-8 border-t-4 border-green-600">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">InvoiceMeNow</h3>
              </div>
              
              <ul className="space-y-4">
                {[
                  { icon: '✅', text: 'Zero signup required', emphasis: 'Start instantly' },
                  { icon: '✅', text: 'Purpose-built invoice interface', emphasis: 'No searching needed' },
                  { icon: '✅', text: 'Form-based data entry', emphasis: '< 1 minute per invoice' },
                  { icon: '✅', text: 'Automatic total calculation', emphasis: 'Math done for you' },
                  { icon: '✅', text: 'Tax-compliant invoice format', emphasis: 'Legal & professional' },
                  { icon: '✅', text: 'Direct email delivery built-in', emphasis: 'One-click send' },
                  { icon: '✅', text: 'Stripe payment integration', emphasis: 'Get paid immediately' },
                  { icon: '✅', text: 'Invoice-first design', emphasis: 'Perfect tool for billing' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <span className="text-gray-700 font-medium">{item.text}</span>
                      <span className="block text-sm text-green-700 font-bold mt-1">→ {item.emphasis}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Time Comparison */}
        <div className="mb-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Time to Create One Invoice
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-8 bg-purple-50 rounded-xl border-2 border-purple-200">
              <div className="text-5xl mb-4">⏱️</div>
              <div className="text-5xl font-bold text-purple-600 mb-2">10-15 min</div>
              <div className="text-xl font-semibold text-gray-900 mb-4">Using Canva</div>
              <ul className="text-left text-sm text-gray-700 space-y-2">
                <li>• Find template (2 min)</li>
                <li>• Edit design layers (5 min)</li>
                <li>• Calculate totals manually (2 min)</li>
                <li>• Download PDF (1 min)</li>
                <li>• Open email & attach (2 min)</li>
                <li>• Send separately (1 min)</li>
              </ul>
            </div>

            <div className="text-center p-8 bg-green-50 rounded-xl border-2 border-green-600 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                15x FASTER
              </div>
              <div className="text-5xl mb-4">⚡</div>
              <div className="text-5xl font-bold text-green-600 mb-2">&lt; 1 min</div>
              <div className="text-xl font-semibold text-gray-900 mb-4">Using InvoiceMeNow</div>
              <ul className="text-left text-sm text-gray-700 space-y-2">
                <li>• Fill form fields (30 sec)</li>
                <li>• Auto-calculate totals (instant)</li>
                <li>• Generate PDF (instant)</li>
                <li>• Send email (10 sec)</li>
                <li>• Done! ✓</li>
                <li className="text-green-700 font-bold">• Save 14 minutes per invoice</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Why People Choose Canva (And Why They Shouldn't) */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            "But I Like Canva's Design Options..."
          </h2>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 mb-6">
                We understand. Canva is a fantastic graphic design tool. But here's the truth about using it for invoices:
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="bg-red-50 rounded-xl p-6 border-l-4 border-red-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">❌ The Canva Invoice Reality</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li><strong>Design Freedom = Compliance Risk:</strong> Pretty doesn't mean legal. Tax authorities have specific invoice requirements that graphic templates often miss.</li>
                    <li><strong>Beauty Takes Time:</strong> That gradient background? Those custom fonts? They add zero value to getting paid but cost you 10+ minutes per invoice.</li>
                    <li><strong>Manual Work:</strong> Every. Single. Time. You edit text boxes, recalculate totals, align elements. It's tedious.</li>
                    <li><strong>Not Built for Invoicing:</strong> No email integration, no payment links, no client management. You need 3 more tools to finish the job.</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">✅ The InvoiceMeNow Approach</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li><strong>Professional by Default:</strong> Our invoices are clean, compliant, and respected by clients and tax authorities alike.</li>
                    <li><strong>Speed = Profit:</strong> Create 15 invoices in the time it takes to make 1 in Canva. More time for billable work.</li>
                    <li><strong>Automation:</strong> Auto-calculations, auto-formatting, auto-numbering. You focus on the business details, we handle the rest.</li>
                    <li><strong>Complete Solution:</strong> Generate → Email → Accept Payment. All in one place. No tool-switching required.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Feature-by-Feature Breakdown
          </h2>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Feature</th>
                    <th className="px-6 py-4 text-center">InvoiceMeNow</th>
                    <th className="px-6 py-4 text-center">Canva</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    ['Invoice Creation Speed', '< 1 minute', '10-15 minutes'],
                    ['Automatic Calculations', '✓ Yes', '✗ Manual'],
                    ['Tax Compliance', '✓ Built-in', '⚠️ Not guaranteed'],
                    ['Direct Email Sending', '✓ One-click', '✗ Download + email'],
                    ['Payment Collection', '✓ Stripe built-in', '✗ No integration'],
                    ['Multi-Currency Support', '✓ 5 currencies', '✗ Manual only'],
                    ['Invoice Numbering', '✓ Auto-generated', '✗ Manual'],
                    ['Client Management', '✓ Optional account', '✗ None'],
                    ['Mobile Optimization', '✓ Fully responsive', '⚠️ App required'],
                    ['Learning Curve', 'None', 'Moderate'],
                    ['Cost', '$0 forever', '$0 (Free) / $15/mo (Pro)'],
                    ['Purpose', 'Invoice-first', 'General design']
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">{row[0]}</td>
                      <td className="px-6 py-4 text-center text-green-600 font-bold">{row[1]}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Testimonial-Style Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            What Freelancers Say After Switching
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "I wasted hours every week in Canva. Switching to InvoiceMeNow literally gave me 5+ hours back per month.",
                name: "Sarah K.",
                role: "Freelance Designer"
              },
              {
                quote: "Canva is great for logos and social posts. But for invoices? Total overkill. This is so much faster.",
                name: "Marcus T.",
                role: "IT Consultant"
              },
              {
                quote: "My accountant actually thanked me for switching. The invoices are now tax-compliant and properly formatted.",
                name: "Jennifer L.",
                role: "Marketing Agency Owner"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-600">
                <div className="text-blue-600 text-4xl mb-4">"</div>
                <p className="text-gray-700 mb-4 italic">{testimonial.quote}</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Stop Using a Design Tool for Invoicing</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Switch to a purpose-built invoice generator. Save time, look professional, get paid faster.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create Your First Invoice Now
          </Link>
          <p className="mt-4 text-sm text-blue-100">No Canva account needed • No design skills required • Free forever</p>
        </div>
      </div>
    </div>
  )
}

export default CanvaAlternativePage
